import React, {useEffect, useState} from "react";
import { Button, Card, Grid, Stack, Typography } from "@mui/joy";
import IProfile from "../../types/profile.type";
import {Experimental_CssVarsProvider as CssVarsProvider} from "@mui/material/styles/ThemeProviderWithVars";
import {FormControl, InputLabel, MenuItem, Select, SelectChangeEvent, TextField} from "@mui/material";
import {GridSaveAltIcon} from "@mui/x-data-grid";
import {useFormik} from "formik";
import * as Yup from "yup";
import { updateBankAccount } from "../../services/profile.service";
import showNotification from "../utils/Notify";
import IBankAccount from "../../types/bank-account.type";
import AssuredWorkloadIcon from "@mui/icons-material/AssuredWorkload";
import CancelIcon from "@mui/icons-material/Cancel";
import BorderColorIcon from "@mui/icons-material/BorderColor";

interface Props {
  userId: any,
  profile: IProfile | undefined,
}

const BankDataForm: React.FC<Props> = (props) => {
  const { profile } = props;
  const [edit, setEdit] = useState(false);

  const initialValues: IBankAccount = {
    accountNumber: "",
    alias: "",
    bankName: "",
    cbu: "",
    holderId: "",
    holderName: "",
    type: "SAVING"
  };

  const validationSchema = Yup.object({
    accountNumber: Yup.string().required('El Numero de Cuenta es requerido'),
    alias: Yup.string().required('El Alias es requerido'),
    bankName: Yup.string().required('El Nombre de Banco es requerido'),
    cbu: Yup.string().required('El CBU es requerido'),
    holderId: Yup.string().required('El CUIL del Titular es requerido'),
    holderName: Yup.string().required('El Nombre del Titular es requerido'),
    type: Yup.string().required('El Tipo de Cuenta es requerido'),
  });

  const onSubmit = (bankAccount: IBankAccount) => {
    if (profile?.id) {
      updateBankAccount(profile?.id, bankAccount)
        .then((response) => {
          showNotification({type: 'success', message: 'Se actualizaron los Datos Bancarios.'});
          profile.bankAccount = response.data;
          setEdit(false);
        })
        .catch(error => {
          showNotification({type: 'error', message: error.response.data.message});
        });
    }
  };

  const formik = useFormik({
    enableReinitialize: false,
    initialValues: initialValues,
    onSubmit,
    validationSchema: validationSchema,
  });

  const handleChangeType = (event: SelectChangeEvent) => {
    formik.setFieldValue('type', event.target.value)
  };

  const handleEditCancelClick = () => {
    formik.setTouched({});
    if (edit) {
      setEdit(false);
      if (profile?.bankAccount) {
        formik.setValues(profile.bankAccount);
      } else {
        formik.resetForm();
      }
    } else {
      setEdit(true);
    }
  };

  useEffect(() => {
    if (profile?.bankAccount) {
      formik.setValues(profile.bankAccount);
    }
  }, [profile?.id]);

  return (
    <Card sx={{gap: 0, marginTop: 2}}>
      <Typography level="h4" startDecorator={<AssuredWorkloadIcon/>}>Datos Bancarios</Typography>
      <form className="form" onSubmit={formik.handleSubmit}>
        <Grid container rowSpacing={2} columnSpacing={2} marginBlock={1}>
          <Grid xl={6} md={6} sm={6} xs={6}>
            <CssVarsProvider>
              <TextField
                fullWidth
                id="cbu"
                label="CBU"
                name="cbu"
                variant="filled"
                type={"text"}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.cbu}
                error={!!(formik.touched.cbu && formik.errors.cbu) && edit}
                slotProps={{inputLabel: {shrink: true}, input: {readOnly: !edit}}}
              />
              {(formik.touched.cbu && formik.errors.cbu) && edit ? (
                <span className="Error-alert">{formik.errors.cbu}</span>
              ) : null}
            </CssVarsProvider>
          </Grid>
          <Grid xl={6} md={6} sm={6} xs={6}>
            <CssVarsProvider>
              <TextField
                fullWidth
                id="alias"
                label="Alias"
                name="alias"
                variant="filled"
                type={"text"}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.alias}
                error={!!(formik.touched.alias && formik.errors.alias) && edit}
                slotProps={{inputLabel: {shrink: true}, input: {readOnly: !edit}}}
              />
              {(formik.touched.alias && formik.errors.alias) && edit ? (
                <span className="Error-alert">{formik.errors.alias}</span>
              ) : null}
            </CssVarsProvider>
          </Grid>
          <Grid xl={6} md={6} sm={6} xs={6}>
            <CssVarsProvider>
              <TextField
                fullWidth
                id="accountNumber"
                label="Numero de Cuenta"
                name="accountNumber"
                variant="filled"
                type={"text"}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.accountNumber}
                error={!!(formik.touched.accountNumber && formik.errors.accountNumber) && edit}
                slotProps={{inputLabel: {shrink: true}, input: {readOnly: !edit}}}
              />
              {(formik.touched.accountNumber && formik.errors.accountNumber) && edit ? (
                <span
                  className="Error-alert">{formik.errors.accountNumber}</span>
              ) : null}
            </CssVarsProvider>
          </Grid>
          <Grid xl={6} md={6} sm={6} xs={6}>
            <CssVarsProvider>
              <TextField
                fullWidth
                id="bankName"
                label="Nombre del Banco"
                name="bankName"
                variant="filled"
                type={"text"}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.bankName}
                error={!!(formik.touched.bankName && formik.errors.bankName) && edit}
                slotProps={{inputLabel: {shrink: true}, input: {readOnly: !edit}}}
              />
              {(formik.touched.bankName && formik.errors.bankName) && edit ? (
                <span className="Error-alert">{formik.errors.bankName}</span>
              ) : null}
            </CssVarsProvider>
          </Grid>
          <Grid xl={6} md={6} sm={6} xs={6}>
            <CssVarsProvider>
              <TextField
                fullWidth
                id="holderName"
                label="Nombre del Titular"
                name="holderName"
                variant="filled"
                type={"text"}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.holderName}
                error={!!(formik.touched.holderName && formik.errors.holderName) && edit}
                slotProps={{inputLabel: {shrink: true}, input: {readOnly: !edit}}}
              />
              {(formik.touched.holderName && formik.errors.holderName) && edit ? (
                <span className="Error-alert">{formik.errors.holderName}</span>
              ) : null}
            </CssVarsProvider>
          </Grid>
          <Grid xl={6} md={6} sm={6} xs={6}>
            <CssVarsProvider>
              <FormControl variant="filled" fullWidth>
                <InputLabel id="type-label" shrink>Tipo de Cuenta</InputLabel>
                <Select
                  labelId="type-label"
                  id="type"
                  name="type"
                  label="type"
                  value={formik.values.type}
                  onBlur={formik.handleBlur}
                  onChange={handleChangeType}
                  slotProps={{input: {readOnly: !edit}}}
                >
                  <MenuItem key="SAVING" value="SAVING">Caja de Ahorro</MenuItem>
                  <MenuItem key="CHECKING" value="CHECKING">Cuenta Corriente</MenuItem>
                </Select>
                {(formik.touched.type && formik.errors.type) && edit ? (
                  <span className="Error-alert">{formik.errors.type}</span>
                ) : null}
              </FormControl>
            </CssVarsProvider>
          </Grid>
          <Grid xl={6} md={6} sm={6} xs={6}>
            <CssVarsProvider>
              <TextField
                fullWidth
                id="holderId"
                label="CUIL del Titular"
                name="holderId"
                variant="filled"
                type={"text"}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.holderId}
                error={!!(formik.touched.holderId && formik.errors.holderId) && edit}
                slotProps={{inputLabel: {shrink: true}, input: {readOnly: !edit}}}
              />
              {(formik.touched.holderId && formik.errors.holderId) && edit ? (
                <span className="Error-alert">{formik.errors.holderId}</span>
              ) : null}
            </CssVarsProvider>
          </Grid>
        </Grid>
        <Stack direction="row" justifyContent="flex-end" spacing={2} pt={1}>
          <Button variant="outlined" endDecorator={edit ? <CancelIcon/> : <BorderColorIcon />} onClick={handleEditCancelClick}>{edit ? 'Cancelar' : 'Editar'}</Button>
          <Button type="submit" variant="solid" endDecorator={<GridSaveAltIcon/>} disabled={!edit}>Guardar</Button>
        </Stack>
      </form>
    </Card>
  );
};

export default BankDataForm;
